<template>
  <div class="utility-page invisible-scrollbar">
    <section class="utility-toolbar">
      <v-select
        v-model="activeSearch"
        class="status-filter"
        :items="statusItems"
        item-text="text"
        item-value="value"
        :placeholder="$t('infrastructure.status')"
        clearable
        dense
        solo
        flat
        hide-details
        @change="searchDataUtility"
      />
      <div class="utility-search">
        <input
          v-model.trim="search"
          type="search"
          :placeholder="$t('infrastructure.search')"
          :aria-label="$t('infrastructure.searchUtility')"
          @keydown.enter="searchDataUtility"
        /><button type="button" :aria-label="$t('infrastructure.search')" @click="searchDataUtility">
          <v-icon color="white" size="24">mdi-magnify</v-icon>
        </button>
      </div>
      <button
        class="create-utility"
        type="button"
        @click="OpenDataDialog('create')"
      >
        {{ $t("infrastructure.create") }} +
      </button>
    </section>

    <section class="utility-panel">
      <div class="utility-table-wrap">
        <v-data-table class="utility-table-list" :headers="headers" :items="ListData" :items-per-page="itemsPerPage" item-key="id" hide-default-footer disable-sort>
          <template v-slot:item.no="{ index }">{{ no_run + index + 1 }}</template>
          <template v-slot:item.active="{ item }"><span :class="['status-chip', { inactive: !item.active }]">{{ item.active ? $t("infrastructure.active") : $t("infrastructure.inactive") }}</span></template>
          <template v-slot:item.action="{ item }"><div class="utility-actions"><button type="button" :aria-label="$t('infrastructure.editNamed', { name: item.utilityName || $t('infrastructure.utility') })" @click="OpenDataDialog('edit', item.id)"><img class="utility-action-icon utility-action-icon--edit" src="@/assets/icons/brush.svg" alt="" aria-hidden="true" /></button><button type="button" class="delete" :disabled="!item.active || DeletingUtilityID === item.id" :aria-label="$t('infrastructure.deleteNamed', { name: item.utilityName || $t('infrastructure.utility') })" @click="deleteDataUtilityByID(item.id)"><img class="utility-action-icon utility-action-icon--delete" src="@/assets/icons/bag.svg" alt="" aria-hidden="true" /></button></div></template>
          <template v-slot:no-data><div class="table-empty">{{ $t("infrastructure.noUtilityData") }}</div></template>
        </v-data-table>
      </div>
      <footer class="utility-pagination">
        <v-pagination
          v-model="page"
          :length="pageCount"
          :total-visible="3"
          color="#00368e"
          @input="ChangePage(page)"
        />
        <v-select
          v-model="itemsPerPage"
          class="utility-page-size"
          :items="items"
          dense
          solo
          flat
          hide-details
          @input="ChangePerPage(itemsPerPage)"
        />
      </footer>
    </section>

    <v-dialog
      v-model="UtilityDialog"
      persistent
      max-width="576px"
      @keydown.esc="UtilityDialog = false"
      @input="OnUtilityDialogChange"
    >
      <v-card class="utility-dialog">
        <v-card-title class="utility-dialog__header">
          <span>{{ EditStatus ? $t("infrastructure.editUtility") : $t("infrastructure.createUtility") }}</span>
          <button
            type="button"
            :aria-label="$t('infrastructure.close')"
            @click.stop="UtilityDialog = false"
          >
            <v-icon color="white" size="30">mdi-close</v-icon>
          </button>
        </v-card-title>

        <v-card-text class="utility-dialog__content">
          <label class="utility-dialog__label" for="utility-name">
            {{ $t("infrastructure.utilityName") }}
          </label>
          <input
            id="utility-name"
            ref="utilityNameInput"
            v-model.trim="UtilityName"
            class="utility-dialog__input"
            :class="{ invalid: ValidationErrors.utilityName }"
            type="text"
            @input="ValidationErrors.utilityName = ''"
            @keydown.enter="saveDataUtility"
          />
          <p v-if="ValidationErrors.utilityName" class="utility-dialog__error">
            {{ ValidationErrors.utilityName }}
          </p>

          <label class="utility-dialog__label mt-4" for="utility-name-th">
            {{ $t("infrastructure.utilityNameTH") }}
          </label>
          <input
            id="utility-name-th"
            ref="utilityNameTHInput"
            v-model.trim="UtilityNameTH"
            class="utility-dialog__input"
            :class="{ invalid: ValidationErrors.utilityNameTH }"
            type="text"
            @input="ValidationErrors.utilityNameTH = ''"
            @keydown.enter="saveDataUtility"
          />
          <p v-if="ValidationErrors.utilityNameTH" class="utility-dialog__error">
            {{ ValidationErrors.utilityNameTH }}
          </p>

          <div class="utility-status active-status-control" role="radiogroup" :aria-label="$t('infrastructure.status')">
            <button
              type="button"
              role="radio"
              :aria-checked="Active == '1'"
              :class="{ selected: Active == '1' }"
              @click="Active = '1'"
            >
              {{ $t("infrastructure.active") }}
            </button>
            <button
              type="button"
              role="radio"
              :aria-checked="Active == '2'"
              :class="{ selected: Active == '2' }"
              @click="Active = '2'"
            >
              {{ $t("infrastructure.inactive") }}
            </button>
          </div>

          <div class="utility-dialog__actions">
            <button
              type="button"
              class="cancel"
              @click.stop="UtilityDialog = false"
            >
              {{ $t("infrastructure.cancel") }}
            </button>
            <button
              type="button"
              class="utility-submit"
              :disabled="IsSubmitting"
              @click="saveDataUtility"
            >
              {{ EditStatus ? $t("infrastructure.update") : $t("infrastructure.create") }}
            </button>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="LoadingDialog" persistent width="auto">
      <v-card
        class="pa-6 d-flex flex-column align-center justify-center"
        elevation="8"
        rounded="xl"
      >
        <v-progress-circular
          indeterminate
          color="indigo"
          size="70"
          width="6"
        ></v-progress-circular>
        <span class="mt-4 text-subtitle-1 font-weight-medium"
          >{{ $t("infrastructure.loading") }}</span
        >
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: "Utility",
  components: {},
  data() {
    return {
      url: enurl.apiUrl,
      LoadingDialog: false,
      IsSubmitting: false,
      DeletingUtilityID: null,
      items: [10, 15, 20, 50, 100],
      pageCount: 0,
      page: 1,
      itemsPerPage: 10,
      search: "",
      activeSearch: "Active",
      no_run: 0,

      ListData: [],
      UtilityID: null,
      UtilityNameTH: "",
      UtilityName: "",
      Active: 1,
      EditStatus: false,
      UtilityDialog: false,
      ValidationErrors: {
        utilityName: "",
        utilityNameTH: "",
      },
    };
  },
  computed: {
    statusItems() {
      return [
        { text: this.$t("infrastructure.active"), value: "Active" },
        { text: this.$t("infrastructure.inactive"), value: "InActive" },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("message.No"),
          value: "no", sortable: false, align: "start",
        },
        {
          text: this.$t("infrastructure.utilityName"), value: "utilityName", align: "start",
          sortable: false,
        },
        {
          text: this.$t("infrastructure.utilityNameTH"), value: "utilityNameTH", align: "start",
          sortable: false,
        },
        {
          text: this.$t("infrastructure.status"), value: "active",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("infrastructure.action"), value: "action",
          align: "center",
          sortable: false,
        },
      ];
    },
  },

  async mounted() {
    let self = this;
    await self.getDataUtilityList();
  },

  methods: {
    async getDataUtilityList() {
      this.LoadingDialog = true;
      const temp = {
        search: this.search || "",
        page: this.page,
        perpage: this.itemsPerPage,
        active:
          this.activeSearch == "Active"
            ? true
            : this.activeSearch == "InActive"
            ? false
            : null,
      };
      try {
        const response = await axios.post(`${this.url}Utility/getDataUtilityList`, temp);
        this.throwIfSessionExpired(response);
        if (response.data.status != 0) throw new Error(response.data.message);
        this.ListData = response.data.data.listData || [];
        this.pageCount = response.data.data.totalPages ?? response.data.data.count;
        this.no_run = (this.page - 1) * this.itemsPerPage;
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.LoadingDialog = false;
      }
    },

    async ChangePage(value) {
      this.page = value;
      await this.getDataUtilityList();
    },

    async ChangePerPage(value) {
      this.page = 1;
      this.itemsPerPage = value;
      await this.getDataUtilityList();
    },

    async searchDataUtility() {
      this.page = 1;
      await this.getDataUtilityList();
    },

    async getDataUtilityDetailByID(id) {
      this.LoadingDialog = true;
      try {
        const response = await axios.post(
          `${this.url}Utility/getDataUtilityDetailByID`,
          { id },
        );
        this.throwIfSessionExpired(response);
        if (response.data.status != 0) throw new Error(response.data.message);
        this.BindingData(response.data.data);
        this.focusUtilityName();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.LoadingDialog = false;
      }
    },

    validateUtility() {
      this.ValidationErrors.utilityName = "";
      this.ValidationErrors.utilityNameTH = "";
      if (!this.UtilityName || !this.UtilityName.trim())
        this.ValidationErrors.utilityName = this.$t("infrastructure.utilityNameRequired");
      else if (this.UtilityName.trim().length > 200)
        this.ValidationErrors.utilityName = this.$t("infrastructure.utilityNameTooLong");
      if (!this.UtilityNameTH || !this.UtilityNameTH.trim())
        this.ValidationErrors.utilityNameTH = this.$t("infrastructure.utilityNameTHRequired");
      else if (this.UtilityNameTH.trim().length > 200)
        this.ValidationErrors.utilityNameTH = this.$t("infrastructure.utilityNameTHTooLong");
      return this.ValidationErrors.utilityName || this.ValidationErrors.utilityNameTH;
    },

    async saveDataUtility() {
      if (this.IsSubmitting) return;
      const validationMessage = this.validateUtility();
      if (validationMessage) {
        this.focusFirstInvalidField();
        return;
      }

      this.IsSubmitting = true;
      this.LoadingDialog = true;
      const payload = {
        utilityName: this.UtilityName.trim(),
        utilityNameTH: this.UtilityNameTH.trim(),
        active: String(this.Active) === "1",
      };
      if (this.EditStatus) payload.id = this.UtilityID;

      try {
        const endpoint = this.EditStatus ? "Utility/editDataUtility" : "Utility/addDataUtility";
        const response = await axios.post(`${this.url}${endpoint}`, payload);
        this.throwIfSessionExpired(response);
        if (response.data.status != 0) throw new Error(response.data.message);
        await Swal.fire({ icon: "success", title: this.EditStatus ? this.$t("infrastructure.updated") : this.$t("infrastructure.created"), text: this.EditStatus ? this.$t("infrastructure.updateSuccess") : this.$t("infrastructure.createSuccess"), showConfirmButton: false, timer: 1500 });
        if (!this.EditStatus) this.page = 1;
        this.CloseDialog();
        await this.getDataUtilityList();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.IsSubmitting = false;
        this.LoadingDialog = false;
      }
    },

    async deleteDataUtilityByID(id) {
      if (this.DeletingUtilityID !== null) return;
      const confirmation = await Swal.fire({
        icon: "warning",
        title: this.$t("infrastructure.deleteUtilityTitle"),
        text: this.$t("infrastructure.deleteUtilityText"),
        showCancelButton: true,
        confirmButtonText: this.$t("infrastructure.delete"),
        cancelButtonText: this.$t("infrastructure.cancel"),
        confirmButtonColor: "#d00000",
      });
      if (!confirmation.isConfirmed) return;

      this.DeletingUtilityID = id;
      this.LoadingDialog = true;
      try {
        const response = await axios.post(`${this.url}Utility/deleteDataUtilityByID`, { id });
        this.throwIfSessionExpired(response);
        if (response.data.status != 0) throw new Error(response.data.message);
        if (this.ListData.length === 1 && this.page > 1) this.page -= 1;
        await Swal.fire({ icon: "success", title: this.$t("infrastructure.deleted"), text: this.$t("infrastructure.deleteSuccess"), showConfirmButton: false, timer: 1500 });
        await this.getDataUtilityList();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.DeletingUtilityID = null;
        this.LoadingDialog = false;
      }
    },

    BindingData(data) {
      let self = this;
      self.UtilityID = data.id;
      self.UtilityName = data.name;
      self.UtilityNameTH = data.nameTH;
      self.Active =
        data.active == true ? "1" : data.active == false ? "2" : null;
      self.EditStatus = true;
      self.UtilityDialog = true;
    },

    OpenDataDialog(type, id) {
      let self = this;
      if (type == "create") {
        self.UtilityID = null;
        self.UtilityNameTH = "";
        self.UtilityName = "";
        self.Active = "1";
        self.EditStatus = false;
        self.UtilityDialog = true;
        self.focusUtilityName();
      } else {
        self.getDataUtilityDetailByID(id);
      }
    },

    CloseDialog() {
      this.UtilityDialog = false;
    },

    OnUtilityDialogChange(value) {
      if (value) return;

      this.UtilityID = null;
      this.UtilityNameTH = "";
      this.UtilityName = "";
      this.Active = "1";
      this.EditStatus = false;
      this.ValidationErrors.utilityName = "";
      this.ValidationErrors.utilityNameTH = "";
    },

    focusUtilityName() {
      this.$nextTick(() => this.$refs.utilityNameInput?.focus());
    },

    focusFirstInvalidField() {
      if (this.ValidationErrors.utilityName) this.focusUtilityName();
      else if (this.ValidationErrors.utilityNameTH)
        this.$nextTick(() => this.$refs.utilityNameTHInput?.focus());
    },

    throwIfSessionExpired(response) {
      const message = String(response?.data?.message || "");
      const normalizedMessage = message.toLowerCase();
      if (normalizedMessage.includes("session") && normalizedMessage.includes("expired")) {
        const error = new Error(message);
        error.isSessionExpired = true;
        this.$store.dispatch("ForceLogout", message);
        throw error;
      }
    },

    async showRequestError(error) {
      if (error?.isSessionExpired || error?.response?.status === 401) return;
      await Swal.fire({
        icon: "warning",
        width: 900,
        text:
          error?.response?.data?.message ||
          error?.message ||
          this.$t("infrastructure.connectionError"),
      });
    },
  },
};
</script>
<style scoped>
.utility-page {
  min-height: calc(100vh - 125px);
  padding: var(--workspace-content-top) 0 60px;
  background: #f6f6f6;
}
.utility-toolbar {
  display: grid;
  grid-template-columns: 385px minmax(360px, 660px) 157px;
  justify-content: center;
  align-items: center;
  gap: 23px;
  margin-bottom: 66px;
}
.status-filter {
  border-radius: 60px;
}
.status-filter ::v-deep .v-input__slot {
  min-height: 60px;
  padding: 0 20px 0 26px !important;
  border-radius: 60px !important;
  background: #fff !important;
  box-shadow: 0 6px 6px rgba(0, 0, 0, 0.2) !important;
}
.status-filter ::v-deep .v-select__selection,
.status-filter ::v-deep input {
  color: #6f6f6f;
  font-size: 18px;
}
.utility-search {
  display: flex;
  height: 60px;
  align-items: center;
  padding: 6px 9px 6px 36px;
  border-radius: 60px;
  background: #fff;
  box-shadow: 0 6px 6px rgba(0, 0, 0, 0.2);
}
.utility-search input {
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  color: #191c1e;
  font-size: 20px;
}
.utility-search input::placeholder {
  color: #6f6f6f;
  opacity: 1;
}
.utility-search button {
  display: grid;
  width: 45px;
  height: 45px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #00378e;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}
.create-utility {
  height: 60px;
  border: 0;
  border-radius: 60px;
  background: #00378e;
  box-shadow: 0 6px 6px rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 20px;
  font-weight: 500;
}
.utility-panel {
  position: relative;
  box-sizing: border-box;
  min-height: 722px;
  padding: 22px 18px 34px;
  border-radius: 30px;
  background: #fff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}
.utility-table-wrap {
  overflow-x: auto;
  border-radius: 30px 30px 0 0;
}
.utility-table-list {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  color: #4c4c4c;
  font-size: 15px;
}
.utility-table-list ::v-deep .v-data-table__wrapper { overflow-x: auto; }
.utility-table-list ::v-deep table { min-width: 900px; table-layout: fixed; }
.utility-table-list ::v-deep .v-data-table-header { background: #00378e; }
.utility-table-list ::v-deep .v-data-table-header th {
  height: 72px;
  padding: 0 24px !important;
  border-bottom: 0 !important;
  color: #fff !important;
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
}
.utility-table-list ::v-deep tbody td {
  height: 68px;
  padding: 0 24px !important;
  border-bottom: 0 !important;
  color: #4c4c4c;
  font-size: 15px;
  vertical-align: middle;
}
.utility-table-list ::v-deep th:nth-child(1),
.utility-table-list ::v-deep td:nth-child(1) {
  width: 8%;
  text-align: center;
}
.utility-table-list ::v-deep th:nth-child(2),
.utility-table-list ::v-deep td:nth-child(2),
.utility-table-list ::v-deep th:nth-child(3),
.utility-table-list ::v-deep td:nth-child(3) {
  width: 30%;
  text-align: left;
}
.utility-table-list ::v-deep th:nth-child(4),
.utility-table-list ::v-deep td:nth-child(4),
.utility-table-list ::v-deep th:nth-child(5),
.utility-table-list ::v-deep td:nth-child(5) {
  width: 16%;
  text-align: center;
}
.utility-table-list ::v-deep td:nth-child(2),
.utility-table-list ::v-deep td:nth-child(3) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.utility-table-list ::v-deep tbody tr { background:#fff; }
.utility-table-list ::v-deep tbody tr:nth-child(even) { background:#f6f6f6; }
.table-empty { padding:32px 16px; color:#595959; text-align:center; }
.utility-table-list thead {
  height: 81px;
  background: #00378e;
  color: #fff;
  font-size: 18px;
  font-weight: 400;
}
.utility-table-list th,
.utility-table-list td {
  padding: 0 38px;
  text-align: left;
}
.utility-table-list th:first-child,
.utility-table-list td:first-child {
  width: 190px;
}
.utility-table-list th:nth-child(3),
.utility-table-list td:nth-child(3) {
  width: 430px;
  text-align: center;
}
.utility-table-list th:last-child,
.utility-table-list td:last-child {
  width: 200px;
  text-align: center;
}
.utility-table-list tbody tr {
  height: 65px;
  background: #fff;
}
.utility-table-list tbody tr:nth-child(even) {
  background: #f6f6f6;
}
.status-chip {
  display: inline-flex;
  min-width: 94px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: #91d589;
  color: #3f6840;
  font-size: 15px;
}
.status-chip.inactive {
  background: #f1aaaa;
  color: #812424;
}
.utility-actions {
  display: flex;
  height: 65px;
  align-items: center;
  justify-content: center;
  gap: 26px;
}
.utility-actions button {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #ff8500;
}
.utility-actions button.delete {
  background: #d00000;
}
.utility-actions button:disabled {
  cursor: not-allowed;
  filter: grayscale(1);
  opacity: 0.42;
}
.utility-action-icon { display:block; object-fit:contain; }
.utility-action-icon--edit { width:18px; height:18px; }
.utility-action-icon--delete { width:24px; height:24px; }
.utility-pagination {
  position: absolute;
  right: 18px;
  bottom: 66px;
  left: 18px;
  display: flex;
  height: 59px;
  align-items: center;
  justify-content: center;
}
.utility-page-size {
  position: absolute;
  right: 75px;
  width: 133px;
  max-width: 133px;
}
.utility-page-size ::v-deep .v-input__slot {
  min-height: 50px;
  border-radius: 30px !important;
  background: #f6f6f6 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25) !important;
}
.utility-pagination ::v-deep .v-pagination__item,
.utility-pagination ::v-deep .v-pagination__navigation {
  width: 49px;
  min-width: 49px;
  height: 49px;
  border-radius: 10px;
}
.utility-dialog {
  overflow: hidden;
  border-radius: 16px !important;
  background: #ffffff;
  font-family: Inter, sans-serif;
}
.utility-dialog__header {
  position: relative;
  min-height: 74px;
  justify-content: center;
  padding: 0 76px;
  background: #00378e;
  color: #fff;
  font-size: 22px;
  font-weight: 600;
}
.utility-dialog__header button {
  position: absolute;
  top: 15px;
  right: 22px;
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 0;
  background: transparent;
}
.utility-dialog__content {
  min-height: 458px;
  padding: 22px 30px 32px !important;
  color: #191c1e !important;
}
.utility-dialog__label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.utility-dialog__input {
  width: 100%;
  height: 56px;
  padding: 0 22px;
  border: 1px solid #dfe3ec;
  border-radius: 28px;
  outline: none;
  color: #191c1e;
  font-size: 16px;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}
.utility-dialog__input:focus {
  border-color: #00378e;
  box-shadow: 0 0 0 3px rgba(0, 55, 142, 0.14);
}
.utility-dialog__input.invalid {
  border-color: #c62828;
}
.utility-dialog__input.invalid:focus {
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.14);
}
.utility-dialog__error {
  margin: 6px 12px 0;
  color: #b71c1c;
  font-size: 12px;
  line-height: 18px;
}
.utility-status {
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  margin-top: 28px;
  padding-right: 4px;
}
.utility-status button {
  width: 158px;
  height: 56px;
  border: 0;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.24);
  color: #4c4c4c;
  font-size: 16px;
  font-weight: 500;
  transition: box-shadow 180ms ease, color 180ms ease;
}
.utility-status button.selected {
  border: 3px solid #00378e;
  color: #282828;
  box-shadow: none;
}
.utility-status button:focus-visible,
.utility-dialog__actions button:focus-visible,
.utility-dialog__header button:focus-visible {
  outline: 3px solid rgba(0, 55, 142, 0.3);
  outline-offset: 2px;
}
.utility-dialog__actions {
  display: flex;
  justify-content: space-between;
  gap: 48px;
  margin-top: 70px;
  padding: 0 64px;
}
.utility-dialog__actions button {
  width: 190px;
  height: 64px;
  border: 0;
  border-radius: 32px;
  font-size: 19px;
}
.utility-dialog__actions .cancel {
  background: #ebeae9;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
  color: #00378e;
}
.utility-dialog__actions .utility-submit {
  background: #00378e;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
  color: #fff;
}
.utility-dialog__actions button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
@media (prefers-reduced-motion: reduce) {
  .utility-dialog__input,
  .utility-status button {
    transition: none;
  }
}
@media (max-width: 1200px) {
  .utility-toolbar {
    grid-template-columns: 1fr 1.5fr 157px;
  }
  .utility-page {
    padding-right: 24px;
    padding-left: 24px;
  }
}
@media (max-width: 760px) {
  .utility-page {
    padding: 24px 12px;
  }
  .utility-toolbar {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 28px;
  }
  .create-utility {
    width: 157px;
    justify-self: end;
  }
  .utility-panel {
    padding: 12px;
    border-radius: 16px;
  }
  .utility-pagination {
    padding-bottom: 70px;
  }
  .utility-page-size {
    right: 50%;
    transform: translateX(50%);
  }
  .utility-dialog {
    border-radius: 16px !important;
  }
  .utility-dialog__content {
    min-height: 0;
    padding: 20px 18px 24px !important;
  }
  .utility-status {
    justify-content: center;
    gap: 12px;
  }
  .utility-status button {
    width: calc(50% - 6px);
  }
  .utility-dialog__actions {
    gap: 12px;
    margin-top: 48px;
    padding: 0;
  }
  .utility-dialog__actions button {
    width: calc(50% - 6px);
  }
}

/* Compact desktop density; dialog and mobile sizing remain unchanged. */
@media (min-width: 761px) {
  .utility-dialog__header {
    min-height: 67px;
    padding: 0 68px;
    font-size: 20px;
  }

  .utility-dialog__header button {
    top: 13px;
    right: 20px;
    width: 40px;
    height: 40px;
  }

  .utility-dialog__content {
    min-height: 412px;
    padding: 20px 27px 29px !important;
  }

  .utility-dialog__input {
    height: 50px;
    padding: 0 20px;
    border-radius: 25px;
    font-size: 14px;
  }

  .utility-status {
    gap: 22px;
    margin-top: 25px;
  }

  .utility-status button {
    width: 142px;
    height: 50px;
    border-radius: 25px;
    font-size: 14px;
  }

  .utility-dialog__actions {
    gap: 43px;
    margin-top: 63px;
    padding: 0 58px;
  }

  .utility-dialog__actions button {
    width: 171px;
    height: 58px;
    border-radius: 29px;
    font-size: 17px;
  }

  .utility-page {
    min-height: calc(100vh - 104px);
    padding: 36px 0;
  }

  .utility-toolbar {
    grid-template-columns: minmax(240px, 320px) minmax(360px, 560px) 126px;
    gap: 16px;
    margin-bottom: 28px;
  }

  .status-filter ::v-deep .v-input__slot {
    min-height: 46px;
    padding: 0 17px !important;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16) !important;
  }

  .status-filter ::v-deep .v-select__selection,
  .status-filter ::v-deep input,
  .utility-search input {
    font-size: 15px;
  }

  .utility-search,
  .create-utility {
    height: 46px;
  }

  .utility-search {
    padding: 5px 6px 5px 22px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  }

  .utility-search button {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    box-shadow: none;
  }

  .create-utility {
    font-size: 16px;
  }

  .utility-panel {
    min-height: 360px;
    padding: 20px 14px 90px;
    border-radius: 16px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  .utility-table-wrap {
    border-radius: 12px 12px 0 0;
  }

  .utility-table-list ::v-deep .v-data-table-header th {
    height: 56px;
    padding: 0 20px !important;
    font-size: 15px;
  }

  .utility-table-list ::v-deep tbody td {
    height: 50px;
    padding: 0 20px !important;
    font-size: 13px;
  }

  .status-chip {
    min-width: 78px;
    height: 26px;
    font-size: 13px;
  }

  .utility-actions {
    height: 50px;
    gap: 16px;
  }

  .utility-actions button {
    width: 34px;
    height: 34px;
  }

  .utility-pagination {
    right: 14px;
    bottom: 24px;
    left: 14px;
    height: 44px;
  }

  .utility-page-size {
    right: 30px;
    width: 112px;
    max-width: 112px;
  }

  .utility-page-size ::v-deep .v-input__slot {
    min-height: 42px;
  }

  .utility-pagination ::v-deep .v-pagination__item,
  .utility-pagination ::v-deep .v-pagination__navigation {
    width: 40px;
    min-width: 40px;
    height: 40px;
    border-radius: 8px;
  }
}
</style>
